import { useState } from 'react'
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material'
import { supabase } from '../../lib/supabase'
import { isPasswordValid } from '../../lib/password'
import { PasswordRequirements } from '../PasswordRequirements'

/**
 * Real password change, via Supabase Auth. This project has
 * GOTRUE_SECURITY_UPDATE_PASSWORD_REQUIRE_CURRENT_PASSWORD enabled, so
 * `auth.updateUser` itself rejects a password change unless `current_password`
 * is included in the same call — the server verifies it, not the client.
 */
export const ChangePasswordCard = () => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const passwordsMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword
  const canSubmit =
    currentPassword.length > 0 && isPasswordValid(newPassword) && newPassword === confirmPassword

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setSuccess(false)

    if (!currentPassword) {
      setError('Enter your current password.')
      return
    }
    if (!isPasswordValid(newPassword)) {
      setError('New password does not meet all the requirements.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.')
      return
    }

    setIsSubmitting(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
        current_password: currentPassword,
      })
      if (updateError) throw updateError

      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setSuccess(true)
    } catch (err: any) {
      const code: string = err?.code ?? ''
      const message: string = err?.message ?? ''
      if (code === 'invalid_credentials' || /current password/i.test(message)) {
        setError('Current password is incorrect.')
      } else if (
        code === 'same_password' ||
        /same.*password|different from the old/i.test(message)
      ) {
        setError('New password should be different from your current password.')
      } else if (code === 'weak_password') {
        setError(message || 'New password does not meet the requirements.')
      } else if (code === 'session_expired' || /session|jwt|expired/i.test(message)) {
        setError('Your session expired. Please log out and back in, then try again.')
      } else {
        setError(message || 'Failed to update password. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You'll need your current password to set a new one.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">Password updated.</Alert>}

            <TextField
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
            />
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
            />
            <PasswordRequirements password={newPassword} />
            <TextField
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              error={passwordsMismatch}
              helperText={passwordsMismatch ? 'Passwords do not match' : ' '}
            />
            <Box>
              <Button type="submit" variant="contained" disabled={isSubmitting || !canSubmit}>
                {isSubmitting ? 'Updating…' : 'Update password'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  )
}
